import { useMemo } from 'react'

import {
  useFilters, useModals, useNumericParams, useSetState,
} from '@domain-group/react-hooks'

import { ModalContext } from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'
import type { PageContextPayload } from '@contexts/pageContext'

import type { AttachmentModel } from '@models/types'

import handleCallbackAction from '@functions/handleCallbackAction'
import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import type { ShowCreateOrDuplicateTrainingModule } from '@hooks/useTrainingModule'

import { TrainingLessonModel } from '@models/types'

import CreateOrDuplicateTrainingModuleModal from '@modals/CreateOrDuplicateTrainingModuleModal'
import CreateOrEditAttachmentModal from '@modals/CreateOrEditAttachmentModal'
import CreateTrainingLessonModal from '@modals/CreateTrainingLessonModal'

import PageContent from './components/PageContent'

const defaultState = {
  showCreateOrDuplicateTrainingModuleModal: false,
  showCreateOrEditAttachmentModal: false,
  showCreateTrainingLessonModal: false,
}

type SetState = (state: Partial<typeof defaultState>) => void

const callbacks = (component: keyof typeof componentCallbacks, setState: SetState) => {
  const componentCallbacks = {
    CreateOrDuplicateTrainingModuleModal: {
      closeModal: () => setState({ showCreateOrDuplicateTrainingModuleModal: false }),
      createTrainingModule: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      duplicateTrainingModule: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateOrEditAttachmentModal: {
      closeModal: () => setState({ showCreateOrEditAttachmentModal: false }),
      deleteAttachment: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      updateAttachment: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
    CreateTrainingLessonModal: {
      closeModal: () => setState({ showCreateTrainingLessonModal: false }),
      createTrainingLesson: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
    },
  }
  return componentCallbacks[component] || {}
}

const Details = () => {
  const [state, setState] = useSetState(defaultState)
  const {
    showCreateOrDuplicateTrainingModuleModal,
    showCreateOrEditAttachmentModal,
    showCreateTrainingLessonModal,
  } = state

  const { trainingModuleId } = useNumericParams()

  const { callbacks: filterCallbacks, defaultFilters } = useFilters()

  const modalContext = useModals({ setState })

  const {
    callbacks: { setModalData },
  } = modalContext

  const pageContext: PageContextPayload = useMemo(
    () => ({
      callbacks: {
        ...filterCallbacks,
        showCreateOrDuplicateTrainingModuleModal: (payload: ShowCreateOrDuplicateTrainingModule) => {
          setModalData('CreateOrDuplicateTrainingModuleModal', payload)
          setState({ showCreateOrDuplicateTrainingModuleModal: true })
        },
        showCreateOrEditAttachmentModal: (payload: { attachment: AttachmentModel }) => {
          setModalData('CreateOrEditAttachmentModal', payload)
          setState({ showCreateOrEditAttachmentModal: true })
        },
        showCreateTrainingLessonModal: (payload: { trainingLesson: TrainingLessonModel }) => {
          setModalData('CreateTrainingLessonModal', payload)
          setState({ showCreateTrainingLessonModal: true })
        },
        updateTrainingModule: (payload: HandleCallbackActionParams) => handleCallbackAction(payload),
      },
      defaultFilters,
    }),
    [],
  )

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent trainingModuleId={trainingModuleId} />

        <CreateOrDuplicateTrainingModuleModal
          callbacks={callbacks('CreateOrDuplicateTrainingModuleModal', setState)}
          showModal={showCreateOrDuplicateTrainingModuleModal}
        />

        <CreateOrEditAttachmentModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CreateOrEditAttachmentModal', setState)}
          showModal={showCreateOrEditAttachmentModal}
        />

        <CreateTrainingLessonModal
          // @ts-expect-error TS2322
          callbacks={callbacks('CreateTrainingLessonModal', setState)}
          showModal={showCreateTrainingLessonModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default Details
